// client/src/services/eventStream.js
//
// Client SDK for the server's SSE channel (/api/events/stream). Opens an
// EventSource, auto-reconnects on drop, and exposes a tiny subscribe API
// so views can react to alarm.*/report.*/export.* events without polling.
//
// Usage:
//   import eventStream from '../services/eventStream.js';
//   const off = eventStream.subscribe('alarm.triggered', (payload, type) => { ... });
//   off();   // unsubscribe later
//
//   // wildcard:
//   eventStream.subscribePattern('alarm.*', (payload, type) => { ... });
//
//   // ask the server to also send chatty events you want
//   eventStream.setIncludes(['pool', 'query']);   // before .connect()
//
// Auth: pulls JWT from localStorage('token'). Reconnects automatically with
// exponential backoff. Events from before the reconnect that you missed are
// NOT replayed — for that, do an initial REST fetch on view mount and use
// SSE for live updates from then on. (Classic "fetch + subscribe" pattern.)

import { API_URL } from '../config.js';

const subscribers = new Map();      // type -> Set<handler>
const wildcardSubs = [];            // [{ re, handler }]

let source = null;
let backoffMs = 1000;
const BACKOFF_MAX_MS = 30_000;
let manualClose = false;
let includes = [];                  // ['pool', 'query']
let connectedAt = null;

function deliver(type, payload) {
  // exact
  const set = subscribers.get(type);
  if (set) {
    for (const h of set) {
      try { h(payload, type); }
      catch (err) { console.error(`[eventStream] handler for "${type}" threw:`, err); }
    }
  }
  // wildcard
  for (const sub of wildcardSubs) {
    if (sub.re.test(type)) {
      try { sub.handler(payload, type); }
      catch (err) { console.error('[eventStream] wildcard handler threw:', err); }
    }
  }
}

function buildUrl() {
  const token = localStorage.getItem('token');
  if (!token) return null;
  // API_URL ends in /api per the rest of the codebase
  const base = `${API_URL}/events/stream`;
  const params = new URLSearchParams({ token });
  if (includes.length) params.set('include', includes.join(','));
  return `${base}?${params}`;
}

function attachListeners(es) {
  // Auto-bind known event types as they're discovered. We can't pre-enumerate
  // them, but EventSource fires 'message' for any event line — we re-dispatch
  // based on the `type` field in the envelope.
  // We also register named listeners for the most common types for native
  // EventTarget routing (faster than parsing JSON twice).

  const onAny = (ev) => {
    let envelope;
    try { envelope = JSON.parse(ev.data); }
    catch { return; }
    if (!envelope?.type) return;
    deliver(envelope.type, envelope.payload || envelope);
  };

  // Catch-all (events without an event: line — defensive)
  es.onmessage = onAny;

  // Common event types (saves a tiny amount of work since they go via
  // EventTarget routing instead of dispatching twice through onmessage)
  const commonTypes = [
    'system.connected',
    'alarm.triggered', 'alarm.cleared', 'alarm.acknowledged', 'alarm.shelved', 'alarm.unshelved',
    'report.generated', 'report.failed',
    'export.queued', 'export.started', 'export.progress', 'export.done', 'export.failed', 'export.cancelled',
    'notification.sent',
    'pool.stats', 'pool.breaker',
    'tenant.quotaExceeded'
  ];
  for (const t of commonTypes) es.addEventListener(t, onAny);

  es.onopen = () => {
    backoffMs = 1000;             // reset backoff after a clean connect
    connectedAt = Date.now();
  };

  es.onerror = () => {
    // EventSource auto-reconnects, but we want a backoff ceiling. Close
    // and reopen manually so we control the cadence.
    if (manualClose) return;
    try { es.close(); } catch {}
    source = null;
    setTimeout(() => connect(), backoffMs);
    backoffMs = Math.min(BACKOFF_MAX_MS, backoffMs * 2);
  };
}

function connect() {
  if (source) return;
  if (typeof EventSource === 'undefined') {
    console.warn('[eventStream] EventSource not supported in this browser');
    return;
  }
  const url = buildUrl();
  if (!url) {
    // No token — try again later when user logs in
    setTimeout(connect, 2000);
    return;
  }
  manualClose = false;
  try {
    source = new EventSource(url, { withCredentials: false });
    attachListeners(source);
  } catch (err) {
    console.error('[eventStream] connect failed:', err);
    setTimeout(connect, backoffMs);
    backoffMs = Math.min(BACKOFF_MAX_MS, backoffMs * 2);
  }
}

function disconnect() {
  manualClose = true;
  if (source) {
    try { source.close(); } catch {}
    source = null;
  }
}

// ─── Public API ────────────────────────────────────────────────

function subscribe(type, handler) {
  if (!subscribers.has(type)) subscribers.set(type, new Set());
  subscribers.get(type).add(handler);
  // Lazy connect on first subscriber
  if (!source && !manualClose) connect();
  return () => {
    const set = subscribers.get(type);
    if (set) {
      set.delete(handler);
      if (set.size === 0) subscribers.delete(type);
    }
  };
}

function subscribePattern(pattern, handler) {
  // Same translation as the server-side bus: '.' literal, '*' matches any
  // non-dot run.
  const re = new RegExp('^' + pattern.split('.').map(seg =>
    seg === '*' ? '[^.]+' : seg.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  ).join('\\.') + '$');
  const sub = { re, handler };
  wildcardSubs.push(sub);
  if (!source && !manualClose) connect();
  return () => {
    const i = wildcardSubs.indexOf(sub);
    if (i !== -1) wildcardSubs.splice(i, 1);
  };
}

function setIncludes(arr) {
  includes = Array.isArray(arr) ? arr.slice() : [];
  // If already connected, reconnect so the new ?include= takes effect
  if (source) {
    disconnect();
    manualClose = false;
    connect();
  }
}

function status() {
  return {
    connected:   !!source,
    connectedAt,
    subscribers: [...subscribers.keys()],
    wildcardCount: wildcardSubs.length,
    includes:    [...includes]
  };
}

// Auto-disconnect on logout
window.addEventListener?.('storage', (e) => {
  if (e.key === 'token' && !e.newValue) disconnect();
});

const eventStream = { connect, disconnect, subscribe, subscribePattern, setIncludes, status };
export default eventStream;
